/**
 * Componente NotificationCenter
 * Centro de notificações e alertas do sistema
 * 
 * @author Equipe GestaoeCRM
 * @date 02/10/2025
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  X, 
  AlertTriangle, 
  Info, 
  CheckCircle, 
  XCircle,
  Clock,
  Activity,
  Pause,
  Play,
  Settings,
  Volume2,
  VolumeX,
  History
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useInactivityAlert } from '../hooks/useInactivityAlert';
import { formatDuration } from '../services/workSessionApi';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationApi, Notification as BackendNotification } from '../services/notificationApi';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export type NotificationType = 'info' | 'success' | 'warning' | 'error' | 'inactivity';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  persistent?: boolean;
  actions?: NotificationAction[];
}

export interface NotificationAction {
  label: string;
  action: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

interface NotificationCenterProps {
  className?: string;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxNotifications?: number;
  enableInactivityAlerts?: boolean;
  enableSounds?: boolean;
}

interface NotificationItemProps {
  notification: Notification;
  onClose: (id: string) => void;
  onMarkAsRead: (id: string) => void;
}

// ============================================================================
// COMPONENTE NOTIFICATION ITEM
// ============================================================================

const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onClose, 
  onMarkAsRead 
}) => {
  const getIcon = (type: NotificationType) => {
    const iconMap = {
      info: Info,
      success: CheckCircle,
      warning: AlertTriangle,
      error: XCircle,
      inactivity: Clock,
    };
    return iconMap[type] || Info;
  };

  const getColorClasses = (type: NotificationType) => {
    const colorMap = {
      info: 'bg-blue-50 border-blue-200 text-blue-800',
      success: 'bg-green-50 border-green-200 text-green-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      inactivity: 'bg-orange-50 border-orange-200 text-orange-800',
    };
    return colorMap[type] || colorMap.info;
  };

  const Icon = getIcon(notification.type);
  const colorClasses = getColorClasses(notification.type);

  return (
    <div 
      className={`p-4 rounded-lg border-l-4 shadow-sm transition-all duration-300 hover:shadow-md ${colorClasses} ${
        !notification.read ? 'ring-2 ring-blue-200' : ''
      }`}
      onClick={() => !notification.read && onMarkAsRead(notification.id)}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <Icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-medium truncate">
                {notification.title}
              </h4>
              
              <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                {notification.timestamp.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            
            <p className="text-sm text-gray-600 mb-3">
              {notification.message}
            </p>
            
            {/* Ações da notificação */}
            {notification.actions && notification.actions.length > 0 && (
              <div className="flex items-center space-x-2">
                {notification.actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.action();
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      action.variant === 'primary'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : action.variant === 'danger'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Botão de fechar */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose(notification.id);
          }}
          className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  className = '',
  position = 'top-right',
  maxNotifications = 5,
  enableInactivityAlerts = true,
  enableSounds = true
}) => {
  const [localNotifications, setLocalNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(enableSounds);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Buscar notificações do backend (apenas não lidas por padrão)
  const { data: backendNotifications, isLoading } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: () => notificationApi.getNotifications({ read: false, limit: 50 }),
    enabled: !!user,
    refetchInterval: 15000, // Atualizar a cada 15 segundos (melhorado de 30s)
    staleTime: 5000, // Considerar dados frescos por 5 segundos (melhorado de 10s)
    refetchOnWindowFocus: true, // Recarregar quando a janela recebe foco
    refetchOnReconnect: true, // Recarregar quando reconecta
  });

  // Converter notificações do backend para o formato local
  useEffect(() => {
    if (backendNotifications?.data) {
      const convertedNotifications: Notification[] = backendNotifications.data.map((notif: BackendNotification) => ({
        id: notif.id,
        type: (notif.type as NotificationType) || 'info',
        title: notif.title,
        message: notif.message,
        timestamp: new Date(notif.createdAt),
        read: notif.read,
        persistent: true, // Notificações do backend são persistentes até serem marcadas como lidas
      }));

      // Mesclar com notificações locais (de inatividade)
      setLocalNotifications(prev => {
        const inactivityNotifications = prev.filter(n => n.type === 'inactivity');
        return [...convertedNotifications, ...inactivityNotifications];
      });
    }
  }, [backendNotifications]);

  // Mutação para marcar como lida
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationApi.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });

  // Mutação para deletar
  const deleteMutation = useMutation({
    mutationFn: (notificationId: string) => notificationApi.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });
  
  // Hook de inatividade
  const [inactivityState, inactivityActions] = useInactivityAlert({
    enabled: !!user, // Só habilitar se usuário estiver logado
    inactivityThreshold: 15,
    warningThreshold: 10,
    autoPauseThreshold: 30,
    enableAutoPause: false,
    enableBrowserNotifications: true,
  });

  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================

  /** Adicionar nova notificação */
  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      
      // Limitar número de notificações
      if (updated.length > maxNotifications) {
        return updated.slice(0, maxNotifications);
      }
      
      return updated;
    });

    // Tocar som se habilitado
    if (soundEnabled && notification.type !== 'info') {
      playNotificationSound(notification.type);
    }

    // Auto-remover notificações não persistentes após 10 segundos
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, 10000);
    }

    return newNotification.id;
  };

  /** Remover notificação */
  const removeNotification = (id: string) => {
    const notification = localNotifications.find(n => n.id === id);
    if (notification && notification.type !== 'inactivity') {
      // É notificação do backend - chamar API para deletar
      deleteMutation.mutate(id);
    } else {
      // É notificação local - apenas remover do estado
      setLocalNotifications(prev => prev.filter(n => n.id !== id));
    }
  };

  /** Marcar notificação como lida */
  const markAsRead = (id: string) => {
    // Verificar se é notificação do backend ou local
    const notification = localNotifications.find(n => n.id === id);
    if (notification && notification.type !== 'inactivity') {
      // É notificação do backend - chamar API
      markAsReadMutation.mutate(id);
    } else {
      // É notificação local - apenas atualizar estado
      setLocalNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: true } : n)
      );
    }
  };

  /** Marcar todas como lidas */
  const markAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
      // Também marcar notificações locais como lidas
      setLocalNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      // console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  /** Limpar todas as notificações (apenas locais, backend não deve ser deletado) */
  const clearAll = () => {
    // Remover apenas notificações locais (inatividade)
    setLocalNotifications(prev => prev.filter(n => n.type === 'inactivity'));
  };

  /** Tocar som de notificação */
  const playNotificationSound = (type: NotificationType) => {
    if (!soundEnabled) return;

    // Criar contexto de áudio
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Configurar frequência baseada no tipo
    const frequencies = {
      info: 440,
      success: 523,
      warning: 349,
      error: 277,
      inactivity: 392,
    };

    oscillator.frequency.setValueAtTime(frequencies[type] || 440, audioContext.currentTime);
    oscillator.type = 'sine';

    // Configurar volume e duração
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  // ============================================================================
  // EFEITOS DE INATIVIDADE
  // ============================================================================

  // Monitorar estado de inatividade
  useEffect(() => {
    if (!enableInactivityAlerts) return;

    if (inactivityState.showWarning && inactivityState.hasActiveSessions) {
      const existingInactivityNotification = localNotifications.find(
        n => n.type === 'inactivity' && !n.read
      );

      if (!existingInactivityNotification) {
        addNotification({
          type: 'inactivity',
          title: 'Inatividade Detectada',
          message: `Você está inativo há ${formatDuration(inactivityState.inactiveTime / 60)} com sessões ativas.`,
          persistent: true,
          actions: [
            {
              label: 'Estou Aqui',
              action: () => {
                inactivityActions.markAsActive();
                // Remover notificações de inatividade
                setLocalNotifications(prev => prev.filter(n => n.type !== 'inactivity'));
              },
              variant: 'primary',
            },
            {
              label: 'Pausar Sessões',
              action: async () => {
                await inactivityActions.pauseActiveSessions();
                setLocalNotifications(prev => prev.filter(n => n.type !== 'inactivity'));
              },
              variant: 'secondary',
            },
          ],
        });
      }
    }
  }, [
    inactivityState.showWarning,
    inactivityState.inactiveTime,
    inactivityState.hasActiveSessions,
    enableInactivityAlerts,
    localNotifications,
    inactivityActions,
  ]);

  // ============================================================================
  // POSICIONAMENTO
  // ============================================================================

  const getPositionClasses = () => {
    const positions = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
    };
    return positions[position];
  };

  // Contar notificações não lidas (backend + locais)
  const backendUnreadCount = backendNotifications?.unread || 0;
  const localUnreadCount = localNotifications.filter(n => !n.read && n.type === 'inactivity').length;
  const unreadCount = backendUnreadCount + localUnreadCount;
  const notifications = localNotifications;

  return (
    <>
      {/* Botão de notificações */}
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Notificações"
        >
          <Bell className="h-6 w-6" />
          
          {/* Badge de contagem */}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Painel de notificações */}
      {isOpen && (
        <div className={`fixed z-50 w-96 max-h-96 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden ${getPositionClasses()} ${className}`}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Bell className="h-5 w-5 mr-2" />
              Notificações
              {unreadCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-800 text-[10px] font-medium rounded-full">
                  {unreadCount}
                </span>
              )}
            </h3>
            
            <div className="flex items-center space-x-2">
              {/* Controle de som */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title={soundEnabled ? 'Desabilitar sons' : 'Habilitar sons'}
              >
                {soundEnabled ? (
                  <Volume2 className="h-4 w-4" />
                ) : (
                  <VolumeX className="h-4 w-4" />
                )}
              </button>
              
              {/* Fechar */}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Lista de notificações */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm">Carregando notificações...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">Nenhuma notificação</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onClose={removeNotification}
                    onMarkAsRead={markAsRead}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Footer com ações */}
          {notifications.length > 0 && (
            <div className="flex items-center justify-between p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
              >
                Marcar todas como lidas
              </button>
              
              <div className="flex items-center gap-3">
                <Link
                  to="/notifications"
                  onClick={() => setIsOpen(false)}
                  className="text-sm text-gray-600 hover:text-gray-700 transition-colors flex items-center gap-1"
                >
                  <History className="h-4 w-4" />
                  Ver histórico
                </Link>
                
                <button
                  onClick={clearAll}
                  className="text-sm text-red-600 hover:text-red-700 transition-colors"
                >
                  Limpar todas
                </button>
              </div>
            </div>
          )}
          
          {/* Footer quando não há notificações */}
          {notifications.length === 0 && !isLoading && (
            <div className="flex items-center justify-center p-3 border-t border-gray-200 bg-gray-50">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="text-sm text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
              >
                <History className="h-4 w-4" />
                Ver histórico completo
              </Link>
            </div>
          )}
        </div>
      )}
      
      {/* Overlay para fechar */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

// ============================================================================
// HOOK PARA USAR O NOTIFICATION CENTER
// ============================================================================

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => [newNotification, ...prev]);
    return newNotification.id;
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  return {
    notifications,
    addNotification,
    removeNotification,
    markAsRead,
  };
};

export default NotificationCenter;